import { makeAutoObservable } from "mobx";
import { utils } from "@intrasync/utils";
import { RootStore, ServiceStore } from "@intrasync/stores";

export interface DispatchMetrics {
  totalLoads: number;
  totalPanels: number;
  totalWeight: number;
  completedLoads: number;
  pendingLoads: number;
  averageLoadWeight: number;
  availableTrailers: number;
  trailerUtilization: number;
}

export interface LoadSummary {
  loadNumber: string;
  loadDate: string;
  trailerNumber: string;
  numberOfPanels: number;
  loadWeight: number;
  status: "pending" | "in_progress" | "completed";
  yardLocation: string;
}

export interface TrailerSummary {
  trailerNumber: string;
  trailerType: string;
  currentStatus: "available" | "in_use" | "maintenance";
  weightLimit: number;
  heightLimit: number;
  widthLimit: number;
  companyOwned: boolean;
}

class DispatchDashboardStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  get jobNumber() {
    const routerStore = this.rootStore.routerStore;
    if (!routerStore) return "";
    const {
      params: { jobNumber },
    } = routerStore;
    return jobNumber as number;
  }

  get job() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    if (!jobStore) return null;
    const { entities } = jobStore;

    return entities.find((job) => {
      return utils.isSameJobNumber(
        job.jobNumber as string | number,
        this.jobNumber
      );
    });
  }

  get loads() {
    const loadStore = this.rootStore.loadStore as ServiceStore;
    if (!loadStore?.entities?.length) return [];

    return loadStore.entities
      .filter((load) => String(load.jobNumber) === String(this.jobNumber))
      .sort(
        (a, b) =>
          new Date(a.loadDate as string).getTime() -
          new Date(b.loadDate as string).getTime()
      );
  }

  get panels() {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    if (!panelStore?.entities?.length) return [];

    return panelStore.entities.filter(
      (panel) => String(panel.jobNumber) === String(this.jobNumber)
    );
  }

  get trailers() {
    const trailerStore = this.rootStore.trailerStore as ServiceStore;
    if (!trailerStore?.entities?.length) return [];

    return trailerStore.entities;
  }

  get dispatchMetrics(): DispatchMetrics {
    const totalLoads = this.loads.length;
    const totalPanels = this.panels.length;
    const totalWeight = this.loads.reduce(
      (sum, load) => sum + (Number(load.loadWeight) || 0),
      0
    );
    const completedLoads = this.loads.filter(
      (load) => load.status === "completed"
    ).length;
    const pendingLoads = this.loads.filter(
      (load) => load.status === "pending"
    ).length;
    const averageLoadWeight = totalLoads > 0 ? totalWeight / totalLoads : 0;
    const availableTrailers = this.trailers.filter(
      (trailer) => trailer.currentStatus === "available"
    ).length;
    const trailerUtilization =
      this.trailers.length > 0
        ? ((this.trailers.length - availableTrailers) / this.trailers.length) *
          100
        : 0;

    return {
      totalLoads,
      totalPanels,
      totalWeight,
      completedLoads,
      pendingLoads,
      averageLoadWeight,
      availableTrailers,
      trailerUtilization,
    };
  }

  get loadSummaries(): LoadSummary[] {
    return this.loads.map((load) => ({
      loadNumber: String(load.loadNumber || ""),
      loadDate: String(load.loadDate || ""),
      trailerNumber: String(load.trailerNumber || ""),
      numberOfPanels: Number(load.numberOfPanels) || 0,
      loadWeight: Number(load.loadWeight) || 0,
      status:
        (load.status as "pending" | "in_progress" | "completed") || "pending",
      yardLocation: String(load.yardLocation || ""),
    }));
  }

  get trailerSummaries(): TrailerSummary[] {
    return this.trailers.map((trailer) => ({
      trailerNumber: String(trailer.trailerNumber || ""),
      trailerType: String(trailer.trailerType || ""),
      currentStatus:
        (trailer.currentStatus as "available" | "in_use" | "maintenance") ||
        "available",
      weightLimit: Number(trailer.weightLimit) || 0,
      heightLimit: Number(trailer.heightLimit) || 0,
      widthLimit: Number(trailer.widthLimit) || 0,
      companyOwned: Boolean(trailer.companyOwned),
    }));
  }

  get upcomingLoads() {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);

    return this.loads
      .filter((load) => {
        const loadDate = new Date(load.loadDate as string);
        return loadDate >= today && loadDate <= nextWeek;
      })
      .slice(0, 5);
  }

  get loadsByDay() {
    const loadsByDay = new Map<string, number>();

    this.loads.forEach((load) => {
      const dateKey = new Date(load.loadDate as string)
        .toISOString()
        .split("T")[0];
      loadsByDay.set(dateKey, (loadsByDay.get(dateKey) || 0) + 1);
    });

    return Array.from(loadsByDay.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  get trailerUtilizationByType() {
    const typeUtilization = new Map<string, { total: number; inUse: number }>();

    this.trailers.forEach((trailer) => {
      const type = String(trailer.trailerType || "Unknown");
      const current = typeUtilization.get(type) || { total: 0, inUse: 0 };
      current.total += 1;
      if (String(trailer.currentStatus) === "in_use") {
        current.inUse += 1;
      }
      typeUtilization.set(type, current);
    });

    return Array.from(typeUtilization.entries()).map(([type, data]) => ({
      type,
      total: data.total,
      inUse: data.inUse,
      utilization: data.total > 0 ? (data.inUse / data.total) * 100 : 0,
    }));
  }

  refreshData = async () => {
    const loadStore = this.rootStore.loadStore as ServiceStore;
    const trailerStore = this.rootStore.trailerStore as ServiceStore;
    const panelStore = this.rootStore.panelStore as ServiceStore;

    await Promise.all([
      loadStore?.find({ query: { jobNumber: this.jobNumber } }),
      trailerStore?.find(),
      panelStore?.find({ query: { jobNumber: this.jobNumber } }),
    ]);
  };
}

export default DispatchDashboardStore;
